// src/components/ModalShell.jsx
import { useEffect, useMemo, useRef } from "react";
import { Modal } from "./Modal";
import { Ic } from "./Icons";
import { ICONS } from "./iconsData";

/**
 * Каркас модалки: шапка, тело, подвал.
 *
 * Футер собирается одним из трёх способов:
 *
 *   1. `footer` передан явно - используется как есть.
 *   2. `actions` передан - стандартный футер с массивом кнопок слева
 *      от spacer: «Отмена» и «Сохранить» на месте, слева добавляются
 *      переданные действия.
 *   3. Ни то, ни другое - стандартный футер только с «Отмена/Сохранить».
 *
 * subtitle - вторая строка под заголовком. Пробрасывается в Modal,
 * который отвечает за разметку. Подзаголовок не участвует в футере
 * или действиях, не влияет на showSave / saveDisabled - это чистая
 * метаинформация шапки.
 *
 * titleId - пробрасывается в Modal. Одновременно служит сигналом
 * «эта модалка должна быть role="dialog" с aria-labelledby». Если
 * titleId не передан, Modal ведёт себя как раньше - без role/aria
 * на корне. См. комментарий в Modal.jsx.
 *
 * trapFocus - пробрасывается в Modal. Включает фокус-трап внутри
 * модалки. По умолчанию false - поведение остальных модалок не
 * меняется. Специализированные модалки, которым трап нужен,
 * передают trapFocus явно.
 *
 * Enter в поле ввода = Сохранить. Активируется по тому же предикату,
 * что и кнопка «Сохранить» (showSave && !saveDisabled && есть onSave),
 * чтобы не было расхождения: кнопка disabled - Enter молчит; кнопка
 * активна - Enter жмёт её. Правила отсечения (textarea, кнопки,
 * поле в <form>, вложенная модалка) - в Modal.jsx.
 */
export const ModalShell = ({
  title,
  subtitle,
  onClose,
  children,
  onSave,
  saveLabel = "Сохранить",
  width = 640,
  className = "",
  footer = null,
  actions = null,
  showSave = true,
  saveDisabled = false,
  showBack = false,
  backLabel = "Назад",
  onBack = null,
  headerAfter = null,
  bodyRef = null,
  titleId = null,
  trapFocus = false,
}) => {
  /**
   * onSave в ref + булев флаг hasSave на эффект.
   *
   * onSave меняется каждый рендер родителя (handleSubmit(saveHandler)
   * возвращает новую функцию). Прямая подписка на него - пересоздание
   * listener'а на каждый рендер и микро-окно между cleanup и setup,
   * в котором keydown может проскочить. Ref + булев гейт убирают
   * это: эффект переподписывается только когда реально меняется
   * «есть ли кому сохранять».
   */
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const hasSave = showSave && !saveDisabled && typeof onSave === "function";

  const onEnterSubmit = useMemo(
    () =>
      hasSave
        ? () => {
            const cb = onSaveRef.current;
            if (typeof cb === "function") cb();
          }
        : null,
    [hasSave],
  );

  const footerEl =
    footer !== null ? (
      footer
    ) : showSave ? (
      <div className="modal-foot">
        {actions}
        <div className="spacer" />
        <button className="btn ghost" onClick={onClose}>
          Отмена
        </button>
        <button
          className="btn primary"
          onClick={onSave}
          disabled={saveDisabled}
        >
          {saveLabel}
        </button>
      </div>
    ) : null;

  const backButton = showBack ? (
    <button type="button" className="btn ghost sm" onClick={onBack || onClose}>
      <Ic d={ICONS.left} size={14} /> {backLabel}
    </button>
  ) : null;

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      width={width}
      className={className}
      headerBefore={backButton}
      headerAfter={headerAfter}
      footer={footerEl}
      bodyRef={bodyRef}
      onEnterSubmit={onEnterSubmit}
      titleId={titleId}
      trapFocus={trapFocus}
    >
      {children}
    </Modal>
  );
};
